/** Manage only decorative/game-preview videos owned by the supplied component. */
function eggiManageCareVideos(root, selector, { shouldPlay = () => true, releaseDelay = 12000 } = {}) {
  const states = new Map();
  const motion = matchMedia('(prefers-reduced-motion: reduce)');
  let disposed = false;
  const blocked = () => document.hidden || !!document.querySelector('cart-drawer-component dialog[open]');
  const release = (video, state) => {
    clearTimeout(state.timer);
    state.timer = null;
    video.pause();
    const src = video.getAttribute('src');
    if (src) {
      state.src = src;
      video.removeAttribute('src');
      video.load();
    }
    video.dataset.eggiMediaState = 'released';
  };
  const sync = (video, state) => {
    const allowed = state.visible && !blocked() && !motion.matches && shouldPlay(video);
    if (allowed) {
      clearTimeout(state.timer);
      state.timer = null;
      if (!video.getAttribute('src') && state.src) video.src = state.src;
      if (video.getAttribute('src')) {
        video.dataset.eggiMediaState = 'active';
        if (video.paused) video.play().catch(() => {});
      }
    } else {
      video.pause();
      if (video.dataset.eggiMediaState !== 'released') video.dataset.eggiMediaState = 'paused';
      if ((!state.near || blocked()) && !state.timer && video.getAttribute('src')) {
        state.timer = setTimeout(() => {
          if (!disposed && (!state.near || blocked())) release(video, state);
        }, releaseDelay);
      } else if (state.near && !blocked()) {
        clearTimeout(state.timer);
        state.timer = null;
      }
    }
  };
  const refresh = () => states.forEach((state, video) => sync(video, state));
  const handlePageHide = () => states.forEach((state, video) => release(video, state));
  const visibleObserver = new IntersectionObserver(entries => entries.forEach(entry => {
    const state = states.get(entry.target);
    if (!state) return;
    state.visible = entry.isIntersecting;
    sync(entry.target, state);
  }), { threshold: 0.01 });
  const nearObserver = new IntersectionObserver(entries => entries.forEach(entry => {
    const state = states.get(entry.target);
    if (!state) return;
    state.near = entry.isIntersecting;
    sync(entry.target, state);
  }), { rootMargin: '800px 0px' });
  const register = () => {
    states.forEach((state, video) => {
      if (root.contains(video)) return;
      release(video, state);
      video.removeEventListener('play', state.onPlay);
      visibleObserver.unobserve(video);
      nearObserver.unobserve(video);
      states.delete(video);
    });
    root.querySelectorAll(selector).forEach(video => {
      if (states.has(video)) return;
      const state = { visible: false, near: false, src: video.dataset.src || '', timer: null };
      state.onPlay = () => sync(video, state);
      states.set(video, state);
      video.addEventListener('play', state.onPlay);
      visibleObserver.observe(video);
      nearObserver.observe(video);
      sync(video, state);
    });
  };
  const mutationObserver = new MutationObserver(register);
  mutationObserver.observe(root, { childList: true, subtree: true });
  document.addEventListener('visibilitychange', refresh);
  document.addEventListener('dialog:open', refresh, true);
  document.addEventListener('dialog:close', refresh, true);
  window.addEventListener('pagehide', handlePageHide);
  window.addEventListener('pageshow', refresh);
  motion.addEventListener('change', refresh);
  register();
  return {
    refresh,
    disconnect() {
      disposed = true;
      mutationObserver.disconnect();
      visibleObserver.disconnect();
      nearObserver.disconnect();
      document.removeEventListener('visibilitychange', refresh);
      document.removeEventListener('dialog:open', refresh, true);
      document.removeEventListener('dialog:close', refresh, true);
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('pageshow', refresh);
      motion.removeEventListener('change', refresh);
      states.forEach((state, video) => {
        release(video, state);
        video.removeEventListener('play', state.onPlay);
      });
      states.clear();
    },
  };
}

class EggiProductEyes extends HTMLElement {
  constructor() {
    super();
    this.videos = [];
    this.activeVideoIndex = -1;
    this.effects = [];
    this.currentIndex = -1;
    this.isVisible = false;
    this.changeTimer = 0;
    this.cleanupTimer = 0;
    this.pointerFrame = 0;
    this.observer = null;
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  }

  connectedCallback() {
    this.videos = [...this.querySelectorAll('video')];
    const data = this.querySelector('script[type="application/json"]');

    if (this.videos.length < 2 || !data) return;

    try {
      this.effects = JSON.parse(data.textContent).filter(Boolean);
    } catch (_error) {
      this.effects = [];
    }

    if (!this.effects.length) return;

    this.videos.forEach((video) => video.addEventListener('ended', this.handleEnded));
    document.addEventListener('visibilitychange', this.handleVisibilityChange);

    if (this.classList.contains('eggi-product-eyes--gallery')) {
      window.addEventListener('pointermove', this.handlePointerMove, { passive: true });
      window.addEventListener('blur', this.resetPointer);
      this.reduceMotion.addEventListener('change', this.handleMotionPreferenceChange);
    }

    this.observer = new IntersectionObserver(this.handleIntersection, { threshold: 0.18 });
    this.observer.observe(this);
  }

  disconnectedCallback() {
    window.clearTimeout(this.changeTimer);
    window.clearTimeout(this.cleanupTimer);
    window.cancelAnimationFrame(this.pointerFrame);
    this.videos.forEach((video) => video.removeEventListener('ended', this.handleEnded));
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    window.removeEventListener('pointermove', this.handlePointerMove);
    window.removeEventListener('blur', this.resetPointer);
    this.reduceMotion.removeEventListener('change', this.handleMotionPreferenceChange);
    this.observer?.disconnect();
    this.videos.forEach((video) => video.pause());
  }

  get activeVideo() {
    return this.videos[this.activeVideoIndex] || null;
  }

  handleIntersection = ([entry]) => {
    this.isVisible = entry.isIntersecting;

    if (!this.isVisible) {
      window.clearTimeout(this.changeTimer);
      this.videos.forEach((video) => video.pause());
      this.resetPointer();
      return;
    }

    if (this.currentIndex < 0) this.playNext();
    else if (!this.reduceMotion.matches && !document.hidden) this.activeVideo?.play().catch(() => {});
  };

  handleVisibilityChange = () => {
    if (document.hidden) {
      this.videos.forEach((video) => video.pause());
      this.resetPointer();
    }
    else if (this.isVisible && !this.reduceMotion.matches) this.activeVideo?.play().catch(() => {});
  };

  handlePointerMove = (event) => {
    if (!this.isVisible || this.reduceMotion.matches || event.pointerType === 'touch') return;

    const clientX = event.clientX;
    const clientY = event.clientY;
    window.cancelAnimationFrame(this.pointerFrame);
    this.pointerFrame = window.requestAnimationFrame(() => {
      const rect = this.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      const xRatio = Math.max(-1, Math.min(1, (clientX - centerX) / Math.max(rect.width * 1.25, 1)));
      const yRatio = Math.max(-1, Math.min(1, (clientY - centerY) / Math.max(rect.height * 1.25, 1)));
      const maxX = Math.min(8, rect.width * .035);
      const maxY = Math.min(6, rect.height * .03);
      this.style.setProperty('--eggi-eye-x', `${(xRatio * maxX).toFixed(2)}px`);
      this.style.setProperty('--eggi-eye-y', `${(yRatio * maxY).toFixed(2)}px`);
      this.pointerFrame = 0;
    });
  };

  resetPointer = () => {
    window.cancelAnimationFrame(this.pointerFrame);
    this.pointerFrame = 0;
    this.style.setProperty('--eggi-eye-x', '0px');
    this.style.setProperty('--eggi-eye-y', '0px');
  };

  handleMotionPreferenceChange = () => {
    if (this.reduceMotion.matches) this.resetPointer();
  };

  handleEnded = (event) => {
    if (event.currentTarget !== this.activeVideo) return;
    if (!this.isVisible || this.reduceMotion.matches) return;
    this.changeTimer = window.setTimeout(() => this.playNext(), 620 + Math.random() * 980);
  };

  playNext() {
    if (!this.isVisible || !this.effects.length) return;

    let nextIndex = Math.floor(Math.random() * this.effects.length);
    if (this.effects.length > 1 && nextIndex === this.currentIndex) {
      nextIndex = (nextIndex + 1 + Math.floor(Math.random() * (this.effects.length - 1))) % this.effects.length;
    }

    const outgoingVideo = this.activeVideo;
    const incomingVideoIndex = this.activeVideoIndex === 0 ? 1 : 0;
    const incomingVideo = this.videos[incomingVideoIndex];

    const showFrame = () => {
      if (this.reduceMotion.matches) {
        incomingVideo.currentTime = Math.min(0.45, incomingVideo.duration || 0.45);
        incomingVideo.pause();
      } else if (this.isVisible && !document.hidden) {
        incomingVideo.play().catch(() => {});
      }

      requestAnimationFrame(() => {
        incomingVideo.classList.add('is-active');
        outgoingVideo?.classList.remove('is-active');
        this.activeVideoIndex = incomingVideoIndex;

        window.clearTimeout(this.cleanupTimer);
        this.cleanupTimer = window.setTimeout(() => {
          if (!outgoingVideo || outgoingVideo === this.activeVideo) return;
          outgoingVideo.pause();
          outgoingVideo.removeAttribute('src');
          outgoingVideo.load();
        }, 220);
      });
    };

    this.currentIndex = nextIndex;
    incomingVideo.classList.remove('is-active');
    incomingVideo.addEventListener('loadeddata', showFrame, { once: true });
    incomingVideo.src = this.effects[nextIndex];
    incomingVideo.load();
  }
}

class EggiProductFirstBatch extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('[data-eggi-first-batch-video]');
    if (!this.video || !this.video.dataset.src) return;

    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.observer = new IntersectionObserver(this.handleIntersection, {
      rootMargin: '360px 0px',
      threshold: 0.01,
    });
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.video?.pause();
  }

  handleIntersection = ([entry]) => {
    if (!entry.isIntersecting || this.reduceMotion?.matches) {
      this.video?.pause();
      return;
    }

    if (!this.video.src) {
      this.video.src = this.video.dataset.src;
      this.video.load();
    }
    this.video.play().catch(() => {});
  };
}

class EggiProductScreenVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video[data-src]');
    if (!this.video) return;

    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.observer = new IntersectionObserver(this.handleIntersection, {
      rootMargin: '280px 0px',
      threshold: 0.01,
    });
    this.observer.observe(this);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    this.video?.pause();
  }

  handleIntersection = ([entry]) => {
    this.isVisible = entry.isIntersecting;
    this.updatePlayback();
  };

  handleVisibilityChange = () => this.updatePlayback();

  updatePlayback() {
    if (!this.video || !this.isVisible || document.hidden || this.reduceMotion?.matches) {
      this.video?.pause();
      return;
    }

    if (!this.video.src) {
      this.video.src = this.video.dataset.src;
      this.video.load();
    }
    this.video.play().catch(() => {});
  }
}

class EggiProductStory extends HTMLElement {
  connectedCallback() {
    this.countAnimationFrame = 0;
    this.countObserver = null;
    this.countIsActive = false;
    this.countTargets = [...this.querySelectorAll('[data-eggi-count]')];
    this.backToTopButton = this.querySelector('[data-eggi-back-to-top]');
    this.backToTopButton?.addEventListener('click', this.handleBackToTop);
    window.addEventListener('scroll', this.handleStoryScroll, { passive: true });
    window.addEventListener('resize', this.handleStoryScroll, { passive: true });
    this.handleStoryScroll();

    const proof = this.querySelector('.eggi-story-proof');
    if (proof && this.countTargets.length && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.resetCounts();
      this.countObserver = new IntersectionObserver(([entry]) => {
        const isActive = entry.intersectionRatio >= 0.45;
        if (isActive === this.countIsActive) return;
        this.countIsActive = isActive;
        window.cancelAnimationFrame(this.countAnimationFrame);
        if (isActive) this.animateCounts();
        else this.resetCounts();
      }, { threshold: [0, 0.45] });
      this.countObserver.observe(proof);
    }

    this.revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          this.revealObserver.unobserve(entry.target);
        });
      },
      { threshold: 0.14 }
    );

    this.querySelectorAll('[data-eggi-reveal]').forEach((element) => this.revealObserver.observe(element));

    this.stageButtons = [...this.querySelectorAll('[data-eggi-stage-button]')];
    this.stagePanels = [...this.querySelectorAll('[data-eggi-stage-panel]')];
    this.stageButtons.forEach((button) => button.addEventListener('click', this.handleStageClick));

    this.coreOverview = this.querySelector('[data-eggi-core-overview]');
    this.coreSystemButtons = [...this.querySelectorAll('[data-eggi-core-system]')];
    this.coreSystemPanels = [...this.querySelectorAll('[data-eggi-core-panel]')];
    this.setupCoreParticles();
    this.coreSystemButtons.forEach((button) => {
      button.addEventListener('click', this.handleCoreSystemChange);
      button.addEventListener('pointerenter', this.handleCoreSystemChange);
      button.addEventListener('pointerleave', this.handleCoreSystemReset);
      button.addEventListener('focus', this.handleCoreSystemChange);
      button.addEventListener('blur', this.handleCoreSystemReset);
      button.addEventListener('keydown', this.handleCoreSystemKeydown);
    });

    this.setupDetailPanel();

    this.videoObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (entry.isIntersecting && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
      },
      { threshold: 0.35 }
    );
    this.querySelectorAll('[data-eggi-story-video]').forEach((video) => this.videoObserver.observe(video));
  }

  disconnectedCallback() {
    this.countObserver?.disconnect();
    window.cancelAnimationFrame(this.countAnimationFrame);
    this.revealObserver?.disconnect();
    this.videoObserver?.disconnect();
    window.clearTimeout(this.coreSystemResetTimer);
    window.cancelAnimationFrame(this.storyScrollFrame);
    window.removeEventListener('scroll', this.handleStoryScroll);
    window.removeEventListener('resize', this.handleStoryScroll);
    this.backToTopButton?.removeEventListener('click', this.handleBackToTop);
    document.documentElement.classList.remove('eggi-story-is-scrolled');
    this.stageButtons?.forEach((button) => button.removeEventListener('click', this.handleStageClick));
    this.coreSystemButtons?.forEach((button) => {
      button.removeEventListener('click', this.handleCoreSystemChange);
      button.removeEventListener('pointerenter', this.handleCoreSystemChange);
      button.removeEventListener('pointerleave', this.handleCoreSystemReset);
      button.removeEventListener('focus', this.handleCoreSystemChange);
      button.removeEventListener('blur', this.handleCoreSystemReset);
      button.removeEventListener('keydown', this.handleCoreSystemKeydown);
    });
    this.detailTriggers?.forEach((trigger) => trigger.removeEventListener('click', this.handleDetailOpen));
    this.detailPanel?.removeEventListener('click', this.handleDetailBackdrop);
    this.detailPanel?.removeEventListener('close', this.handleDetailClose);
    this.detailPanel?.removeEventListener('cancel', this.handleDetailCancel);
    this.detailCloseButton?.removeEventListener('click', this.handleDetailClose);
    this.detailPrevButton?.removeEventListener('click', this.handleDetailPrevious);
    this.detailNextButton?.removeEventListener('click', this.handleDetailNext);
    document.removeEventListener('keydown', this.handleDetailKeydown);
    document.body.classList.remove('eggi-detail-panel-open');
  }

  animateCounts() {
    this.resetCounts();
    const start = performance.now();
    const duration = 1400;
    const easeOut = (progress) => 1 - ((1 - progress) ** 3);

    const tick = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = easeOut(progress);
      this.countTargets.forEach((target) => {
        const value = Math.round(Number(target.dataset.eggiCount) * eased);
        target.textContent = `${value}${target.dataset.eggiSuffix || ''}`;
      });

      if (progress < 1) {
        this.countAnimationFrame = window.requestAnimationFrame(tick);
      }
    };

    this.countAnimationFrame = window.requestAnimationFrame(tick);
  }

  resetCounts() {
    this.countTargets.forEach((target) => {
      target.textContent = `0${target.dataset.eggiSuffix || ''}`;
    });
  }

  handleStoryScroll = () => {
    if (this.storyScrollFrame) return;
    this.storyScrollFrame = window.requestAnimationFrame(() => {
      const threshold = Math.max(320, this.offsetTop - (window.innerHeight * .45));
      const isScrolled = window.scrollY > threshold;
      this.classList.toggle('is-story-scrolled', isScrolled);
      document.documentElement.classList.toggle('eggi-story-is-scrolled', isScrolled);
      if (this.backToTopButton) {
        this.backToTopButton.setAttribute('aria-hidden', String(!isScrolled));
        this.backToTopButton.tabIndex = isScrolled ? 0 : -1;
      }
      this.storyScrollFrame = 0;
    });
  };

  handleBackToTop = () => {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: 0, behavior: reducedMotion ? 'auto' : 'smooth' });
  };

  handleStageClick = (event) => {
    const index = event.currentTarget.dataset.eggiStageButton;
    this.stageButtons.forEach((button) => {
      const isActive = button.dataset.eggiStageButton === index;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-selected', String(isActive));
    });
    this.stagePanels.forEach((panel) => {
      const isActive = panel.dataset.eggiStagePanel === index;
      panel.classList.toggle('is-active', isActive);
      panel.hidden = !isActive;
    });
  };

  setupDetailPanel() {
    this.detailPanel = this.querySelector('[data-eggi-detail-panel]');
    this.detailTriggers = [...this.querySelectorAll('[data-eggi-detail-open]')];
    this.detailTemplates = [...this.querySelectorAll('[data-eggi-detail-template]')];
    this.detailCloseButton = this.detailPanel?.querySelector('[data-eggi-detail-close]');
    this.detailPrevButton = this.detailPanel?.querySelector('[data-eggi-detail-prev]');
    this.detailNextButton = this.detailPanel?.querySelector('[data-eggi-detail-next]');
    this.detailVisual = this.detailPanel?.querySelector('[data-eggi-detail-visual]');
    this.detailContent = this.detailPanel?.querySelector('[data-eggi-detail-content]');
    this.detailTitle = this.detailPanel?.querySelector('[data-eggi-detail-title]');
    this.detailBreadcrumb = this.detailPanel?.querySelector('[data-eggi-detail-breadcrumb]');
    this.detailPosition = this.detailPanel?.querySelector('[data-eggi-detail-position]');
    if (!this.detailPanel || !this.detailTemplates.length) return;

    this.detailTriggers.forEach((trigger) => trigger.addEventListener('click', this.handleDetailOpen));
    this.detailPanel.addEventListener('click', this.handleDetailBackdrop);
    this.detailPanel.addEventListener('close', this.handleDetailClose);
    this.detailPanel.addEventListener('cancel', this.handleDetailCancel);
    this.detailCloseButton?.addEventListener('click', this.handleDetailClose);
    this.detailPrevButton?.addEventListener('click', this.handleDetailPrevious);
    this.detailNextButton?.addEventListener('click', this.handleDetailNext);
    document.addEventListener('keydown', this.handleDetailKeydown);
  }

  openDetail(id, trigger) {
    if (window.matchMedia('(max-width: 767px)').matches) return;
    const template = this.detailTemplates.find((item) => item.dataset.eggiDetailTemplate === id);
    if (!template || !this.detailPanel) return;
    this.detailReturnFocus = trigger || document.activeElement;
    this.detailId = id;
    this.detailGroup = template.dataset.eggiDetailGroup || '';
    this.renderDetail(template);
    document.body.classList.add('eggi-detail-panel-open');
    if (!this.detailPanel.open) this.detailPanel.showModal();
    this.detailCloseButton?.focus();
  }

  renderDetail(template) {
    const visual = template.content.querySelector('[data-eggi-detail-visual]')?.cloneNode(true);
    const copy = template.content.querySelector('[data-eggi-detail-copy]')?.cloneNode(true);
    const title = copy?.querySelector('h2')?.textContent || 'Explore EGGI';
    const breadcrumb = copy?.querySelector('.eggi-product-module__kicker, [class*="__eyebrow"]')?.textContent || 'EGGI DETAILS';
    if (this.detailVisual) {
      this.detailVisual.className = ['eggi-detail-panel__visual', visual?.className].filter(Boolean).join(' ');
    }
    if (this.detailContent) {
      this.detailContent.className = ['eggi-detail-panel__content', copy?.className].filter(Boolean).join(' ');
    }
    this.detailVisual?.replaceChildren(...(visual ? [...visual.childNodes] : []));
    this.detailContent?.replaceChildren(...(copy ? [...copy.childNodes] : []));
    this.detailTitle.textContent = title;
    this.detailBreadcrumb.textContent = breadcrumb;
    const groupTemplates = this.detailTemplates.filter((item) => item.dataset.eggiDetailGroup === this.detailGroup);
    const index = groupTemplates.indexOf(template);
    this.detailPosition.textContent = `${index + 1} / ${groupTemplates.length}`;
    this.detailPrevButton.disabled = groupTemplates.length < 2;
    this.detailNextButton.disabled = groupTemplates.length < 2;
  }

  moveDetail(direction) {
    const groupTemplates = this.detailTemplates.filter((item) => item.dataset.eggiDetailGroup === this.detailGroup);
    const currentIndex = groupTemplates.findIndex((item) => item.dataset.eggiDetailTemplate === this.detailId);
    if (currentIndex < 0 || groupTemplates.length < 2) return;
    const nextIndex = (currentIndex + direction + groupTemplates.length) % groupTemplates.length;
    const nextTemplate = groupTemplates[nextIndex];
    this.detailId = nextTemplate.dataset.eggiDetailTemplate;
    this.renderDetail(nextTemplate);
  }

  handleDetailOpen = (event) => this.openDetail(event.currentTarget.dataset.eggiDetailOpen, event.currentTarget);

  handleDetailPrevious = () => this.moveDetail(-1);

  handleDetailNext = () => this.moveDetail(1);

  handleDetailBackdrop = (event) => {
    if (event.target === this.detailPanel) this.detailPanel.close();
  };

  handleDetailCancel = () => {
    document.body.classList.remove('eggi-detail-panel-open');
  };

  handleDetailClose = () => {
    if (this.detailPanel?.open) this.detailPanel.close();
    document.body.classList.remove('eggi-detail-panel-open');
    this.detailReturnFocus?.focus?.();
    this.detailReturnFocus = null;
  };

  handleDetailKeydown = (event) => {
    if (!this.detailPanel?.open) return;
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.moveDetail(-1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.moveDetail(1);
    }
  };

  setupCoreParticles() {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const particleCount = 18;
    this.coreSystemButtons.forEach((button, systemIndex) => {
      if (button.querySelector('.eggi-core-system__particles')) return;
      const layer = document.createElement('span');
      layer.className = 'eggi-core-system__particles';
      layer.setAttribute('aria-hidden', 'true');
      for (let index = 0; index < particleCount; index += 1) {
        const particle = document.createElement('i');
        const angle = ((index / particleCount) * Math.PI * 2) + (systemIndex * .34);
        const radius = 46 + ((index * 7) % 12);
        particle.style.setProperty('--particle-x', `${50 + (Math.cos(angle) * radius)}%`);
        particle.style.setProperty('--particle-y', `${50 + (Math.sin(angle) * radius)}%`);
        particle.style.setProperty('--particle-size', `${2 + (index % 3)}px`);
        particle.style.setProperty('--particle-delay', `${-(index * .17)}s`);
        particle.style.setProperty('--particle-duration', `${2.2 + ((index % 5) * .28)}s`);
        layer.append(particle);
      }
      button.prepend(layer);
    });

    if (reducedMotion) return;
    this.coreSystemPanels.forEach((panel) => {
      const svg = panel.querySelector(':scope > svg');
      if (!svg || svg.querySelector('.eggi-core-path-particle')) return;
      [...svg.querySelectorAll('path')].forEach((path, index) => {
        for (let stream = 0; stream < 2; stream += 1) {
          const particle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
          const motion = document.createElementNS('http://www.w3.org/2000/svg', 'animateMotion');
          particle.setAttribute('class', 'eggi-core-path-particle');
          particle.setAttribute('r', stream ? '1.35' : '1.9');
          motion.setAttribute('path', path.getAttribute('d'));
          motion.setAttribute('dur', `${2.2 + ((index % 3) * .28)}s`);
          motion.setAttribute('begin', `${-((index * .13) + (stream * 1.05))}s`);
          motion.setAttribute('repeatCount', 'indefinite');
          particle.append(motion);
          svg.append(particle);
        }
      });
    });
  }

  setCoreSystem(system) {
    if (this.coreOverview.dataset.eggiCoreActive === system) return;
    this.coreOverview.dataset.eggiCoreActive = system;

    this.coreSystemButtons.forEach((button) => {
      const isActive = button.dataset.eggiCoreSystem === system;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-selected', String(isActive));
    });

    const targetPanel = this.coreSystemPanels.find((panel) => panel.dataset.eggiCorePanel === system);
    const outgoingPanels = this.coreSystemPanels.filter((panel) => panel.classList.contains('is-active') && panel !== targetPanel);
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!targetPanel) return;
    targetPanel.hidden = false;
    targetPanel.classList.add('is-active');
    const paths = [...targetPanel.querySelectorAll(':scope > svg path')];
    const content = [targetPanel.querySelector('.eggi-core-branches__heading'), ...targetPanel.querySelectorAll('.eggi-core-feature')].filter(Boolean);

    if (!reducedMotion && window.gsap) {
      window.gsap.killTweensOf([...this.coreSystemPanels, ...paths, ...content]);
      outgoingPanels.forEach((panel) => {
        window.gsap.to(panel, {
          autoAlpha: 0,
          duration: .2,
          ease: 'power1.out',
          overwrite: 'auto',
          onComplete: () => {
            panel.classList.remove('is-active');
            panel.hidden = true;
            window.gsap.set(panel, { clearProps: 'opacity,visibility' });
          },
        });
      });
      window.gsap.fromTo(targetPanel, { autoAlpha: 0 }, { autoAlpha: 1, duration: .34, ease: 'power2.out', overwrite: 'auto' });
      window.gsap.fromTo(paths, { autoAlpha: 0 }, { autoAlpha: .72, duration: .52, ease: 'power2.out', overwrite: 'auto' });
      window.gsap.fromTo(content, { autoAlpha: 0, y: 8 }, { autoAlpha: 1, y: 0, duration: .4, delay: .16, stagger: .045, ease: 'power2.out', overwrite: 'auto' });
    } else {
      outgoingPanels.forEach((panel) => {
        panel.classList.remove('is-active');
        panel.hidden = true;
      });
      if (!reducedMotion) {
        paths.forEach((path) => path.animate(
          [{ opacity: 0 }, { opacity: .72 }],
          { duration: 520, easing: 'cubic-bezier(.2, .72, .2, 1)', fill: 'both' }
        ));
        content.forEach((element, index) => element.animate(
          [{ opacity: 0, transform: 'translateY(8px)' }, { opacity: 1, transform: 'translateY(0)' }],
          { duration: 400, delay: 160 + (index * 45), easing: 'cubic-bezier(.2, .72, .2, 1)', fill: 'both' }
        ));
      }
    }
  }

  handleCoreSystemChange = (event) => {
    window.clearTimeout(this.coreSystemResetTimer);
    const system = event.currentTarget.dataset.eggiCoreSystem;
    if (system) this.setCoreSystem(system);
  };

  handleCoreSystemReset = () => {
    window.clearTimeout(this.coreSystemResetTimer);
    this.coreSystemResetTimer = window.setTimeout(() => this.setCoreSystem('all'), 220);
  };

  handleCoreSystemKeydown = (event) => {
    const keys = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp', 'Home', 'End'];
    if (!keys.includes(event.key)) return;
    event.preventDefault();
    const currentIndex = this.coreSystemButtons.indexOf(event.currentTarget);
    const nextIndex = event.key === 'Home'
      ? 0
      : event.key === 'End'
        ? this.coreSystemButtons.length - 1
        : (currentIndex + (event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : -1) + this.coreSystemButtons.length) % this.coreSystemButtons.length;
    this.coreSystemButtons[nextIndex]?.focus();
  };
}

class EggiProductCare extends HTMLElement {
  connectedCallback() {
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.feedVideo = this.querySelector('[data-eggi-care-feed-video]');
    this.snackButtons = [...this.querySelectorAll('[data-eggi-care-snack]')];
    this.reply = this.querySelector('[data-eggi-care-reply]');
    this.status = this.querySelector('[data-eggi-care-status]');
    this.clueButtons = [...this.querySelectorAll('[data-eggi-care-clue]')];
    this.pottyScreen = this.querySelector('[data-eggi-care-potty-screen]');
    this.flushButton = this.querySelector('[data-eggi-care-flush]');
    this.stats = this.stats || { hungry:32, thirsty:58, mood:66, coins:500 };
    this.petScreen = this.querySelector('.eggi-product-care__screen');
    this.petScreen?.addEventListener('pointerenter', this.handlePetEnter);
    this.petScreen?.addEventListener('pointerleave', this.stopPetting);
    this.petScreen?.addEventListener('click', this.handlePetClick);
    this.petScreen?.addEventListener('keydown', this.handlePetKey);
    document.addEventListener('visibilitychange', this.handlePetVisibility);
    this.renderStats();
    this.statObserver?.disconnect();
    this.statObserver = new IntersectionObserver(entries => {
      const card=this.querySelector('.eggi-care-stats');
      if(entries.some(entry=>entry.isIntersecting)) {card?.classList.add('is-visible');this.statObserver.disconnect();}
    },{threshold:.1});
    this.statObserver.observe(this);
    this.feedStage = 'idle';
    this.currentSnackReply = 'Batteries? My favorite!';
    this.currentClue = this.clueButtons.find((button) => button.classList.contains('is-selected')) || this.clueButtons[0];

    this.snackButtons.forEach((button) => button.addEventListener('click', this.handleSnack));
    this.clueButtons.forEach((button) => button.addEventListener('click', this.handleClue));
    this.flushButton?.addEventListener('click', this.handleFlush);
    this.feedVideo?.addEventListener('ended', this.handleFeedEnded);

    this.renderClue();
    this.careMediaLifecycle = eggiManageCareVideos(this, '[data-eggi-care-feed-video], [data-eggi-care-potty-screen] video');
  }

  disconnectedCallback() {
    this.careMediaLifecycle?.disconnect();
    this.statObserver?.disconnect();
    this.stopPetting();
    this.petScreen?.removeEventListener('pointerenter', this.handlePetEnter);
    this.petScreen?.removeEventListener('pointerleave', this.stopPetting);
    this.petScreen?.removeEventListener('click', this.handlePetClick);
    this.petScreen?.removeEventListener('keydown', this.handlePetKey);
    document.removeEventListener('visibilitychange', this.handlePetVisibility);
    this.snackButtons?.forEach((button) => button.removeEventListener('click', this.handleSnack));
    this.clueButtons?.forEach((button) => button.removeEventListener('click', this.handleClue));
    this.flushButton?.removeEventListener('click', this.handleFlush);
    this.feedVideo?.removeEventListener('ended', this.handleFeedEnded);
    this.feedVideo?.pause();
    window.clearTimeout(this.flushTimer);
  }

  renderStats() {
    for (const [key,value] of Object.entries(this.stats)) {
      const number=this.querySelector(`[data-care-stat="${key}"]`);
      const meter=this.querySelector(`[data-care-meter="${key}"]`);
      if(number) number.textContent=value;
      if(meter) meter.value=value;
    }
  }
  petOnce = () => {
    if (this.feedStage === 'feeding' || this.feedStage === 'burp') return;
    this.lastPetAt=Date.now();
    const increase=Math.min(1,100-this.stats.mood);
    this.stats.mood+=increase;
    this.renderStats();
    this.feedStage='petting';
    this.setFeedMedia(this.petScreen.dataset.loveSrc, {loop:true});
    this.setReply(`EGGI loved that. Mood +${increase}`);
    const heart=document.createElement('span');heart.className='eggi-care-heart';heart.textContent=`Mood +${increase}`;heart.setAttribute('aria-hidden','true');this.petScreen.append(heart);
    if(!this.reduceMotion.matches) {
      heart.animate([{opacity:0,transform:'translate(-50%,12px)'},{opacity:1,offset:.3},{opacity:0,transform:'translate(-50%,-35px)'}],{duration:1000}).finished.then(()=>heart.remove()).catch(()=>heart.remove());
    } else setTimeout(()=>heart.remove(),800);
  };
  handlePetEnter = (event) => {
    if(event.pointerType!=='mouse') return;
    this.stopPetting(); this.petOnce();
    this.petTimer=setInterval(this.petOnce,2200);
  };
  handlePetClick = () => { if(Date.now()-(this.lastPetAt||0)>500) this.petOnce(); };
  handlePetKey = (event) => { if(event.key==='Enter'||event.key===' ') {event.preventDefault();this.petOnce();} };
  handlePetVisibility = () => { if(document.hidden) this.stopPetting(); };
  stopPetting = () => {
    clearInterval(this.petTimer);
    if(this.feedStage==='petting') {this.feedStage='idle';this.setFeedMedia(this.dataset.idleCuriousSrc,{loop:true});}
  };
  setFeedMedia(src, { loop = false, muted = true } = {}) {
    if (!this.feedVideo) return;
    this.feedVideo.loop = loop;
    this.feedVideo.muted = muted;
    this.feedVideo.src = src;
    this.feedVideo.load();
    if (!this.reduceMotion.matches) this.feedVideo.play().catch(() => {});
  }

  setReply(message, status = message) {
    if (this.reply) this.reply.textContent = message;
    if (this.status) this.status.textContent = status;
  }

  handleSnack = (event) => {
    const button = event.currentTarget;
    this.stopPetting();
    if (this.feedStage === 'feeding' || this.feedStage === 'burp') return;
    const rules = [["hungry",20,50],["hungry",30,50],["hungry",50,100],["thirsty",50,60]];
    const [stat, amount, cost] = rules[this.snackButtons.indexOf(button)];
    if (this.stats.coins < cost) { this.setReply('Not enough Egg Coins for this snack.'); return; }
    this.stats.coins -= cost;
    this.stats[stat] = Math.min(100, this.stats[stat] + amount);
    this.renderStats();
    this.snackButtons.forEach((item) => {
      const isSelected = item === button;
      item.classList.toggle('is-selected', isSelected);
      item.setAttribute('aria-pressed', String(isSelected));
    });

    this.feedStage = 'feeding';
    this.currentSnackReply = button.dataset.reply || '';
    this.setReply('Mmm… that tickles!');
    this.setFeedMedia(button.dataset.feedSrc, { muted: false });
  };

  handleFeedEnded = () => {
    if (this.feedStage === 'feeding') {
      const burp = new Audio(this.dataset.burpSrc);
      burp.volume = .78;
      burp.play().catch(() => {});
      this.feedStage = 'burp';
      this.setReply(this.currentSnackReply);
      this.setFeedMedia(this.dataset.idleBurpSrc, { muted: true });
      return;
    }

    if (this.feedStage === 'burp') {
      this.feedStage = 'idle';
      this.setFeedMedia(this.dataset.idleCuriousSrc, { loop: true, muted: true });
    }
  };

  handleClue = (event) => {
    this.resetFlush();
    this.currentClue = event.currentTarget;
    this.clueButtons.forEach((button) => {
      const isSelected = button === this.currentClue;
      button.classList.toggle('is-selected', isSelected);
      button.setAttribute('aria-pressed', String(isSelected));
    });
    this.renderClue();
  };

  renderClue = () => {
    if (!this.pottyScreen || !this.currentClue) return;
    if (!this.reduceMotion.matches && this.currentClue.dataset.clueVideo) {
      const video = document.createElement('video');
      video.src = this.currentClue.dataset.clueVideo;
      video.setAttribute('aria-label', `Animated ${this.currentClue.dataset.clueAlt}`);
      video.autoplay = true;
      video.muted = true;
      video.loop = true;
      video.playsInline = true;
      video.preload = 'metadata';
      this.pottyScreen.replaceChildren(video);
      video.play().catch(() => {});
      return;
    }
    const image = document.createElement('img');
    image.src = this.currentClue.dataset.clueImage;
    image.alt = this.currentClue.dataset.clueAlt;
    image.width = 512;
    image.height = 512;
    image.decoding = 'async';
    this.pottyScreen.replaceChildren(image);
  };

  resetFlush = () => {
    window.clearTimeout(this.flushTimer);
    this.flushTimer = null;
    if (this.flushButton) {
      this.flushButton.disabled = false;
      this.flushButton.textContent = 'Flush';
    }
  };

  handleFlush = () => {
    if (!this.pottyScreen || !this.currentClue || this.flushButton?.disabled) return;
    this.flushButton.disabled = true;
    this.flushButton.textContent = 'All clean!';

    if (this.reduceMotion.matches) {
      this.renderClue();
      this.resetFlush();
      return;
    }

    const video = document.createElement('video');
    video.src = this.currentClue.dataset.flushSrc;
    video.autoplay = true;
    video.muted = false;
    video.volume = .78;
    video.playsInline = true;
    video.preload = 'metadata';
    const finishFlush = () => {
      this.renderClue();
      this.resetFlush();
    };
    video.addEventListener('ended', finishFlush, { once: true });
    video.addEventListener('error', finishFlush, { once: true });
    this.pottyScreen.replaceChildren(video);
    video.play().catch(() => {});
    this.flushTimer = window.setTimeout(finishFlush, 6000);
  };
}

class EggiProductChapterNav extends HTMLElement {
  connectedCallback() {
    if(this.parentElement!==document.body){document.body.append(this);return;}
    if (this.navInitialized) return;
    const story = document.querySelector('eggi-product-story[embedded]');
    if (story && !customElements.get('eggi-product-story')) {
      customElements.whenDefined('eggi-product-story').then(() => {
        if (this.isConnected) this.connectedCallback();
      });
      return;
    }
    this.links = [...this.querySelectorAll('[data-eggi-chapter-index]')];
    this.targets = this.links.map((link) => document.querySelector(link.hash));
    this.toggle = this.querySelector('.eggi-product-chapter-nav__toggle');
    this.counter = this.querySelector('[data-eggi-chapter-count]');
    this.activeIndex = 0;
    this.scrollFrame = 0;
    this.lockedIndex = null;
    this.lockTargetY = 0;
    this.lockDeadline = 0;
    this.lockTimer = 0;

    if (!this.links.length || this.targets.some((target) => !target)) return;
    this.navInitialized = true;

    this.links.forEach((link) => {
      link.addEventListener('click', this.handleLinkClick);
      link.addEventListener('pointerenter', this.handleLinkEnter);
      link.addEventListener('focus', this.handleLinkFocus);
    });
    this.toggle?.addEventListener('click', this.handleToggle);
    this.addEventListener('pointerleave', this.handlePointerLeave);
    this.addEventListener('focusout', this.handleFocusOut);
    this.addEventListener('keydown', this.handleKeydown);
    document.addEventListener('pointerdown', this.handleDocumentPointerDown);
    window.addEventListener('scroll', this.handleScroll, { passive: true });
    window.addEventListener('resize', this.handleResize, { passive: true });
    window.addEventListener('wheel', this.handleUserInterrupt, { passive: true });
    window.addEventListener('touchstart', this.handleUserInterrupt, { passive: true });

    const hashIndex = this.links.findIndex((link) => link.hash === window.location.hash);
    if (hashIndex >= 0) this.setActive(hashIndex);
    else this.updateActiveFromScroll();
    this.updateOffset();
  }

  disconnectedCallback() {
    this.navInitialized = false;
    this.links?.forEach((link) => {
      link.removeEventListener('click', this.handleLinkClick);
      link.removeEventListener('pointerenter', this.handleLinkEnter);
      link.removeEventListener('focus', this.handleLinkFocus);
    });
    this.toggle?.removeEventListener('click', this.handleToggle);
    this.removeEventListener('pointerleave', this.handlePointerLeave);
    this.removeEventListener('focusout', this.handleFocusOut);
    this.removeEventListener('keydown', this.handleKeydown);
    document.removeEventListener('pointerdown', this.handleDocumentPointerDown);
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('wheel', this.handleUserInterrupt);
    window.removeEventListener('touchstart', this.handleUserInterrupt);
    window.cancelAnimationFrame(this.scrollFrame);
    window.clearTimeout(this.lockTimer);
  }

  get reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  get headerOffset() {
    const header = document.querySelector('#header-group, header-component, .header-section');
    if (!header) return 20;
    const position = window.getComputedStyle(header).position;
    if (position !== 'fixed' && position !== 'sticky') return 20;
    const rect = header.getBoundingClientRect();
    return Math.max(20, Math.min(160, rect.bottom) + 12);
  }

  updateOffset() {
    document.documentElement.style.setProperty('--eggi-product-chapter-offset', `${Math.round(this.headerOffset)}px`);
  }

  setIndicator(index) {
    this.style.setProperty('--eggi-chapter-index', String(index));
  }

  setActive(index) {
    const nextIndex = Math.max(0, Math.min(index, this.links.length - 1));
    const previousIndex = this.activeIndex;
    this.activeIndex = nextIndex;
    this.links.forEach((link, linkIndex) => {
      if (linkIndex === nextIndex) link.setAttribute('aria-current', 'true');
      else link.removeAttribute('aria-current');
    });
    if (this.counter) this.counter.textContent = `${String(nextIndex + 1).padStart(2, '0')} / ${this.links.length}`;
    if (!this.matches(':hover') && !this.matches(':focus-within')) this.setIndicator(nextIndex);
    if (nextIndex === 0) this.closeMenu(false);
    else if (nextIndex === 1 && previousIndex !== 1 && !window.matchMedia('(max-width: 899px)').matches) this.openMenu();
  }

  updateActiveFromScroll() {
    if (!this.targets?.length || this.lockedIndex !== null) return;
    const activationLine = this.headerOffset + Math.min(window.innerHeight * .3, 220);
    let nextIndex = 0;
    this.targets.forEach((target, index) => {
      if (target.getBoundingClientRect().top <= activationLine) nextIndex = index;
    });
    if (nextIndex !== this.activeIndex) this.setActive(nextIndex);
  }

  releaseScrollLock = () => {
    window.clearTimeout(this.lockTimer);
    this.lockedIndex = null;
    this.updateActiveFromScroll();
  };

  handleLinkClick = (event) => {
    const link = event.currentTarget;
    const index = Number(link.dataset.eggiChapterIndex);
    const target = this.targets[index];
    if (!target) return;

    event.preventDefault();
    this.updateOffset();
    const destination = Math.max(0, window.scrollY + target.getBoundingClientRect().top - this.headerOffset);
    this.lockedIndex = index;
    this.lockTargetY = destination;
    this.lockDeadline = performance.now() + 1800;
    this.setActive(index);
    window.scrollTo({ top: destination, behavior: this.reducedMotion ? 'auto' : 'smooth' });
    window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}${link.hash}`);
    window.clearTimeout(this.lockTimer);
    this.lockTimer = window.setTimeout(this.releaseScrollLock, this.reducedMotion ? 40 : 1800);

    if (window.matchMedia('(max-width: 899px)').matches) this.closeMenu(true);
  };

  handleLinkEnter = (event) => this.setIndicator(Number(event.currentTarget.dataset.eggiChapterIndex));

  handleLinkFocus = (event) => this.setIndicator(Number(event.currentTarget.dataset.eggiChapterIndex));

  handlePointerLeave = () => this.setIndicator(this.activeIndex);

  handleFocusOut = () => {
    window.requestAnimationFrame(() => {
      if (!this.contains(document.activeElement)) this.setIndicator(this.activeIndex);
    });
  };

  handleScroll = () => {
    if (this.scrollFrame) return;
    this.scrollFrame = window.requestAnimationFrame(() => {
      if (this.lockedIndex !== null) {
        this.setActive(this.lockedIndex);
        if (Math.abs(window.scrollY - this.lockTargetY) < 4 || performance.now() > this.lockDeadline) {
          this.releaseScrollLock();
        }
      } else {
        this.updateActiveFromScroll();
      }
      this.scrollFrame = 0;
    });
  };

  handleResize = () => {
    this.updateOffset();
    this.updateActiveFromScroll();
    if (window.matchMedia('(max-width: 899px)').matches) this.closeMenu(false);
  };

  handleUserInterrupt = () => {
    if (this.lockedIndex !== null) this.releaseScrollLock();
  };

  handleToggle = () => {
    const isOpen = !this.classList.contains('is-open');
    if (isOpen) this.openMenu();
    else this.closeMenu(false);
  };

  handleDocumentPointerDown = (event) => {
    if (this.classList.contains('is-open') && !this.contains(event.target)) this.closeMenu(false);
  };

  handleKeydown = (event) => {
    if (event.key === 'Escape' && this.classList.contains('is-open')) {
      event.preventDefault();
      this.closeMenu(true);
      return;
    }

    const currentIndex = this.links.indexOf(document.activeElement);
    if (currentIndex < 0) return;
    const keys = ['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft', 'Home', 'End'];
    if (!keys.includes(event.key)) return;
    event.preventDefault();

    let nextIndex = currentIndex;
    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % this.links.length;
    if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + this.links.length) % this.links.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = this.links.length - 1;
    this.links[nextIndex].focus();
  };

  closeMenu(restoreFocus) {
    this.classList.remove('is-open');
    this.toggle?.setAttribute('aria-expanded', 'false');
    if (restoreFocus) this.toggle?.focus({ preventScroll: true });
  }

  openMenu() {
    this.classList.add('is-open');
    this.toggle?.setAttribute('aria-expanded', 'true');
    this.setIndicator(this.activeIndex);
  }
}

if (!customElements.get('eggi-product-chapter-nav')) customElements.define('eggi-product-chapter-nav', EggiProductChapterNav);
if (!customElements.get('eggi-product-eyes')) customElements.define('eggi-product-eyes', EggiProductEyes);
if (!customElements.get('eggi-product-story')) customElements.define('eggi-product-story', EggiProductStory);
if (!customElements.get('eggi-product-care')) customElements.define('eggi-product-care', EggiProductCare);
if (!customElements.get('eggi-product-first-batch')) customElements.define('eggi-product-first-batch', EggiProductFirstBatch);
if (!customElements.get('eggi-product-screen-video')) customElements.define('eggi-product-screen-video', EggiProductScreenVideo);
